/**
 * NotificationLogger — Sends notification telemetry events via @bingads-webui/instrumentation.
 *
 * Uses instrumentation.writeRawLog() to send events to Kusto.
 * The instrumentation layer handles batching, retry, and transport.
 *
 * Usage:
 *   const logger = new NotificationLogger(instrumentation);
 *   logger.logImpression(notification, 'UnifiedQueue', 1, 10, renderInstanceId);
 */

import type { IInstrumentation as CommonInstrumentation } from '@bingads-webui/common-types';
import {
  ClickType,
  DismissTrigger,
  Surface,
  NotificationMetadata,
  NotificationLogEventInput,
  ITelemetryNotification,
} from './notification-telemetry.types';

export type IInstrumentation = CommonInstrumentation;
type INotificationInstrumentation = Pick<IInstrumentation, 'writeRawLog'>;

export class NotificationLogger {
  /* eslint-disable no-useless-constructor, no-empty-function */
  constructor(private instrumentation: INotificationInstrumentation) {}
  /* eslint-enable no-useless-constructor, no-empty-function */

  logImpression(
    notification: ITelemetryNotification,
    surface: Surface,
    position: number,
    totalCount: number,
    renderInstanceId: string,
  ): void {
    this.log({
      eventType: 'impression',
      ...this.buildMetadata(notification, surface),
      position,
      totalCount,
      renderInstanceId,
    });
  }

  logClick(
    notification: ITelemetryNotification,
    surface: Surface,
    clickType: ClickType,
    renderInstanceId: string,
    clickTarget?: string,
    clickUrl?: string,
    actionIndex?: number,
    position?: number,
    totalCount?: number,
  ): void {
    this.log({
      eventType: 'click',
      ...this.buildMetadata(notification, surface),
      clickType,
      clickTarget,
      clickUrl,
      actionIndex,
      position,
      totalCount,
      renderInstanceId,
    });
  }

  logDismiss(
    notification: ITelemetryNotification,
    surface: Surface,
    trigger: DismissTrigger,
    renderInstanceId: string,
    position?: number,
    totalCount?: number,
  ): void {
    this.log({
      eventType: 'dismiss',
      ...this.buildMetadata(notification, surface),
      dismissTrigger: trigger,
      position,
      totalCount,
      renderInstanceId,
    });
  }

  logDismissAllSummary(
    totalDismissed: number,
    surface: Surface,
    renderInstanceId: string,
  ): void {
    this.log({
      eventType: 'dismiss',
      notificationId: '__dismiss-all__',
      surface,
      severity: '',
      dismissTrigger: DismissTrigger.DismissAll,
      totalDismissed,
      renderInstanceId,
    });
  }

  // ---- private ----

  private log(event: NotificationLogEventInput): void {
    // Use instrumentation.writeRawLog to send via 1DS pipeline
    // logCategory TRACE routes to the clienttrace Kusto table
    // cid, aid, uid, timestamp are auto-injected by ContextProcessor
    this.instrumentation.writeRawLog({
      logCategory: 'TRACE',
      entryType: 'Message',
      api: 'NotificationTelemetry',
      message: event,
    });
  }

  private buildMetadata(notification: ITelemetryNotification, surface: Surface): NotificationMetadata {
    const actionList = notification.actionList || [];
    return {
      notificationId: notification.id || '',
      notificationTypeId: notification.notificationTypeId,
      notificationSource: notification.notificationSource,
      surface,
      severity: notification.severity || 'unknown',
      dismissMode: notification.dismissMode,
      uiDeliveryId: notification.uiDeliveryId,
      title: notification.title,
      message: typeof notification.message === 'string' ? notification.message : undefined,
      messageType: notification.messageType,
      actionCount: actionList.length,
      actions: actionList.map(a => a.text).join('|') || undefined,
    };
  }
}
